import { useState } from 'react';
import { UserCircle, Activity, Plug, Users, Folder, MessageSquare, Network, GraduationCap, Zap, FolderOpen, Database, Globe, Monitor, Search, Plus, Play, Pause, MoreHorizontal, Check, Clock, ArrowUpRight, ChevronDown, X, Lock, Layers, KeyRound, Copy, Pencil, Grid3x3 } from 'lucide-react';
import { TRACKS, GROUP_NAMES } from '../../data/workspaceTracks';
import { PulsesView } from './PulsesView';

// ── In-shell workspace views ─────────────────────────────────────────────────
// Every nav destination in the Giga chat shell renders here rather than routing
// out to the old dashboard. One shell, many views: the sidebar swaps the pane,
// nothing navigates.
// ─────────────────────────────────────────────────────────────────────────────

export type WorkspaceViewId = 'assistants' | 'pulses' | 'connections' | 'groups' | 'tracks' | 'chats' | 'workflows' | 'skills' | 'power-ups' | 'files' | 'tables' | 'browser' | 'viewer';
export const VIEW_META: Record<WorkspaceViewId, {
  title: string;
  blurb: string;
  icon: typeof UserCircle;
  action?: string;
}> = {
  assistants: {
    title: 'Assistants',
    blurb: 'Coworkers you brief once, then delegate to.',
    icon: UserCircle,
    action: 'New assistant'
  },
  pulses: {
    title: 'Pulses',
    blurb: 'Work that runs on a schedule without being asked.',
    icon: Activity,
    action: 'New pulse'
  },
  connections: {
    title: 'Connections',
    blurb: 'Accounts Giga signs into on your behalf.',
    icon: Plug,
    action: 'Connect a tool'
  },
  groups: {
    title: 'Groups',
    blurb: 'Who can see which knowledge. Access follows the group.',
    icon: Users,
    action: 'New group'
  },
  tracks: {
    title: 'Tracks',
    blurb: 'Projects and areas that keep their work together.',
    icon: Folder,
    action: 'New track'
  },
  chats: {
    title: 'Chats',
    blurb: 'Every conversation, searchable.',
    icon: MessageSquare
  },
  workflows: {
    title: 'Workflows',
    blurb: 'Multi-step jobs you run on demand.',
    icon: Network,
    action: 'New workflow'
  },
  skills: {
    title: 'Skills',
    blurb: 'Recorded how-tos Giga reuses across jobs.',
    icon: GraduationCap,
    action: 'Record a skill'
  },
  'power-ups': {
    title: 'Power Ups',
    blurb: 'Extra abilities you can switch on per workspace.',
    icon: Zap
  },
  files: {
    title: 'Files',
    blurb: 'Documents Giga can read and cite.',
    icon: FolderOpen,
    action: 'Upload'
  },
  tables: {
    title: 'Tables',
    blurb: 'Structured data it can query and write rows to.',
    icon: Database,
    action: 'New table'
  },
  browser: {
    title: 'Browser',
    blurb: 'Sites Giga operates on your behalf.',
    icon: Globe
  },
  viewer: {
    title: 'Viewer',
    blurb: 'Watch a run happen live, step by step.',
    icon: Monitor
  }
};
const logo = (slug: string) => `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`;
const ASSISTANTS = [{
  name: 'Inbox triage',
  desc: 'Sorts overnight mail and surfaces only what needs a reply.',
  tools: ['gmail', 'googlecalendar'],
  runs: '412 runs',
  group: 'Leadership',
  private: true,
  active: true
}, {
  name: 'Pipeline analyst',
  desc: 'Watches deal movement and flags renewals going quiet.',
  tools: ['salesforce', 'slack'],
  runs: '96 runs',
  group: 'Revenue',
  private: false,
  active: true
}, {
  name: 'Meeting prep',
  desc: 'Builds a one-pager for every external meeting the night before.',
  tools: ['googlecalendar', 'notion'],
  runs: '188 runs',
  group: 'Marketing',
  private: false,
  active: true
}, {
  name: 'Support summariser',
  desc: 'Turns Intercom threads into weekly themes for product.',
  tools: ['intercom', 'linear'],
  runs: '54 runs',
  group: 'Operations',
  private: false,
  active: false
}];

// The catalogue. `accounts` matters more than a boolean: several tools support
// more than one signed-in account, and knowing there are two is the difference
// between "connected" and "connected as the wrong person".
type ConnCategory = 'Communication' | 'Productivity' | 'CRM & Sales' | 'Engineering' | 'Marketing' | 'Finance' | 'Data' | 'Design' | 'Support';
const CONNECTIONS: {
  name: string;
  slug: string;
  desc: string;
  category: ConnCategory;
  accounts: number;
  account?: string;
  popular?: boolean;
}[] = [{
  name: 'Gmail',
  slug: 'gmail',
  desc: 'Read, draft and send mail from your own address.',
  category: 'Communication',
  accounts: 2,
  account: 'sarah.chen@brightwave.io',
  popular: true
}, {
  name: 'Google Drive',
  slug: 'google-drive',
  desc: 'Search and read documents across shared drives.',
  category: 'Productivity',
  accounts: 1,
  account: 'sarah.chen@brightwave.io',
  popular: true
}, {
  name: 'Google Calendar',
  slug: 'google-calendar',
  desc: 'See availability and schedule on your behalf.',
  category: 'Productivity',
  accounts: 1,
  account: 'sarah.chen@brightwave.io',
  popular: true
}, {
  name: 'Notion',
  slug: 'notion',
  desc: 'A collaborative workspace for docs and wikis.',
  category: 'Productivity',
  accounts: 1,
  account: 'Brightwave workspace',
  popular: true
}, {
  name: 'Slack',
  slug: 'slack',
  desc: 'Workplace messaging — post, read and reply in threads.',
  category: 'Communication',
  accounts: 1,
  account: 'brightwave.slack.com',
  popular: true
}, {
  name: 'Linear',
  slug: 'linear',
  desc: 'Product development and issue tracking.',
  category: 'Engineering',
  accounts: 1,
  account: 'brightwave',
  popular: true
}, {
  name: 'Google Sheets',
  slug: 'google-sheets',
  desc: 'Read and write rows in spreadsheets.',
  category: 'Data',
  accounts: 1,
  account: 'sarah.chen@brightwave.io'
}, {
  name: 'Intercom',
  slug: 'intercom',
  desc: 'Customer service platform for support conversations.',
  category: 'Support',
  accounts: 1,
  account: 'Brightwave'
}, {
  name: 'Salesforce',
  slug: 'salesforce',
  desc: 'The CRM of record for accounts and opportunities.',
  category: 'CRM & Sales',
  accounts: 0,
  popular: true
}, {
  name: 'HubSpot',
  slug: 'hubspot',
  desc: 'Customer platform and CRM for marketing and sales.',
  category: 'CRM & Sales',
  accounts: 0,
  popular: true
}, {
  name: 'Jira',
  slug: 'jira',
  desc: 'Issue and project tracking for software teams.',
  category: 'Engineering',
  accounts: 0,
  popular: true
}, {
  name: 'GitHub',
  slug: 'github',
  desc: 'Code hosting, pull requests and issues.',
  category: 'Engineering',
  accounts: 0
}, {
  name: 'Figma',
  slug: 'figma',
  desc: 'Collaborative interface design and prototyping.',
  category: 'Design',
  accounts: 0
}, {
  name: 'Airtable',
  slug: 'airtable',
  desc: 'Structured bases Giga can query and update.',
  category: 'Data',
  accounts: 0
}, {
  name: 'Stripe',
  slug: 'stripe',
  desc: 'Financial infrastructure — payments and subscriptions.',
  category: 'Finance',
  accounts: 0
}, {
  name: 'Zoom',
  slug: 'zoom',
  desc: 'Meetings, recordings and transcripts.',
  category: 'Communication',
  accounts: 0
}, {
  name: 'Asana',
  slug: 'asana',
  desc: 'Work management for tasks and projects.',
  category: 'Productivity',
  accounts: 0
}, {
  name: 'Mailchimp',
  slug: 'mailchimp',
  desc: 'Email campaigns, audiences and reporting.',
  category: 'Marketing',
  accounts: 0
}, {
  name: 'Dropbox',
  slug: 'dropbox',
  desc: 'File storage Giga can read from and write to.',
  category: 'Productivity',
  accounts: 0
}, {
  name: 'QuickBooks',
  slug: 'quickbooks',
  desc: 'Accounting, invoices and expense records.',
  category: 'Finance',
  accounts: 0
}, {
  name: 'Canva',
  slug: 'canva',
  desc: 'Visual design and collaboration platform.',
  category: 'Design',
  accounts: 0
}, {
  name: 'Monday.com',
  slug: 'monday',
  desc: 'Work management platform for teams.',
  category: 'Productivity',
  accounts: 0
}, {
  name: 'ClickUp',
  slug: 'clickup',
  desc: 'Tasks, docs and goals in one workspace.',
  category: 'Productivity',
  accounts: 0
}, {
  name: 'Zapier',
  slug: 'zapier',
  desc: 'Trigger automations across thousands of apps.',
  category: 'Productivity',
  accounts: 1,
  account: 'brightwave-ops'
}, {
  name: 'Vercel',
  slug: 'vercel',
  desc: 'Deployments, previews and build logs.',
  category: 'Engineering',
  accounts: 0
}, {
  name: 'Sentry',
  slug: 'sentry',
  desc: 'Error monitoring and release health.',
  category: 'Engineering',
  accounts: 1,
  account: 'brightwave'
}, {
  name: 'Supabase',
  slug: 'supabase',
  desc: 'Backend platform with a Postgres database.',
  category: 'Data',
  accounts: 0
}, {
  name: 'Cloudflare',
  slug: 'cloudflare',
  desc: 'DNS, caching and edge configuration.',
  category: 'Engineering',
  accounts: 0
}, {
  name: 'Ahrefs',
  slug: 'ahrefs',
  desc: 'Search rankings, backlinks and keyword data.',
  category: 'Marketing',
  accounts: 1,
  account: 'brightwave.io'
}, {
  name: 'Apollo',
  slug: 'apollo',
  desc: 'Sales intelligence and prospect enrichment.',
  category: 'CRM & Sales',
  accounts: 0
}, {
  name: 'Clay',
  slug: 'clay',
  desc: 'Enrich and research accounts at scale.',
  category: 'CRM & Sales',
  accounts: 0
}, {
  name: 'Webflow',
  slug: 'webflow',
  desc: 'Website building, CMS and publishing.',
  category: 'Marketing',
  accounts: 0
}, {
  name: 'Ramp',
  slug: 'ramp',
  desc: 'Corporate cards, spend and receipts.',
  category: 'Finance',
  accounts: 0
}, {
  name: 'Zendesk',
  slug: 'zendesk',
  desc: 'Ticketing and helpdesk for customer support.',
  category: 'Support',
  accounts: 0
}, {
  name: 'Granola',
  slug: 'granola',
  desc: 'AI notepad that captures meeting notes.',
  category: 'Productivity',
  accounts: 1,
  account: 'sarah.chen@brightwave.io'
}, {
  name: 'Confluence',
  slug: 'confluence',
  desc: 'Team wiki and internal documentation.',
  category: 'Productivity',
  accounts: 0
}];
const CONN_CATEGORIES: ConnCategory[] = ['Communication', 'Productivity', 'CRM & Sales', 'Engineering', 'Marketing', 'Finance', 'Data', 'Design', 'Support'];
const CONN_PAGE_SIZE = 12;

// Which group owns each live credential. Only connected tools appear here —
// an integration nobody has signed into doesn't belong to anyone yet.
const CONN_GROUP: Record<string, string> = {
  Gmail: 'Company-wide',
  'Google Drive': 'Company-wide',
  'Google Calendar': 'Company-wide',
  Notion: 'Product & Engineering',
  Slack: 'Company-wide',
  Linear: 'Product & Engineering',
  'Google Sheets': 'Marketing',
  Intercom: 'Operations',
  Zapier: 'Operations',
  Sentry: 'Product & Engineering',
  Ahrefs: 'Marketing',
  Granola: 'Leadership'
};

// Custom credentials: the escape hatch for anything without a first-party
// integration. Each type asks for a different shape of secret.
const CREDENTIAL_TYPES = [{
  id: 'email',
  label: 'Email & Password',
  desc: 'For tools Giga signs into through the browser.',
  fields: [{
    label: 'Login URL',
    placeholder: 'https://app.example.com/login',
    type: 'url'
  }, {
    label: 'Email',
    placeholder: 'you@company.com',
    type: 'email'
  }, {
    label: 'Password',
    placeholder: '••••••••••••',
    type: 'password'
  }]
}, {
  id: 'api',
  label: 'API Key',
  desc: 'For tools with a token-based API.',
  fields: [{
    label: 'Base URL',
    placeholder: 'https://api.example.com',
    type: 'url'
  }, {
    label: 'API key',
    placeholder: 'sk_live_…',
    type: 'password'
  }, {
    label: 'Header name',
    placeholder: 'Authorization',
    type: 'text'
  }]
}, {
  id: 'sql-string',
  label: 'SQL (connection string)',
  desc: 'One URI for the whole database.',
  fields: [{
    label: 'Connection string',
    placeholder: 'postgres://user:pass@host:5432/db',
    type: 'password'
  }]
}, {
  id: 'sql-fields',
  label: 'SQL (fields)',
  desc: 'Host, port and credentials entered separately.',
  fields: [{
    label: 'Host',
    placeholder: 'db.example.com',
    type: 'text'
  }, {
    label: 'Port',
    placeholder: '5432',
    type: 'text'
  }, {
    label: 'Database',
    placeholder: 'production',
    type: 'text'
  }, {
    label: 'User',
    placeholder: 'readonly',
    type: 'text'
  }, {
    label: 'Password',
    placeholder: '••••••••••••',
    type: 'password'
  }]
}] as const;
const CHATS = [{
  name: 'Inbox catch-up',
  when: 'Today, 09:12',
  preview: 'Three things need you today — Acme redlines, Northwind seats, invoice 2214.'
}, {
  name: 'Tomorrow’s meetings',
  when: 'Today, 08:40',
  preview: 'Four meetings. Two need prep — Acme legal and Northwind kickoff.'
}, {
  name: 'Q3 pipeline review',
  when: 'Yesterday',
  preview: 'Revenue up 34% QoQ, CAC down 12%, one renewal unsigned.'
}, {
  name: 'Renewal risk in Salesforce',
  when: 'Yesterday',
  preview: 'Two accounts have gone quiet for 21+ days.'
}, {
  name: 'Draft reply to Acme legal',
  when: '2 days ago',
  preview: 'Position on the liability cap, plus data residency language.'
}, {
  name: 'Summarise the board deck',
  when: '3 days ago',
  preview: 'Slide 19 has an ask with no numbers behind it.'
}, {
  name: 'Who owns the migration?',
  when: 'Last week',
  preview: 'Priya, per the Linear project and the #eng thread.'
}, {
  name: 'Weekly marketing recap',
  when: 'Last week',
  preview: 'Paid down 8%, organic up 15%, two campaigns ended.'
}];
const WORKFLOWS = [{
  name: 'Tweet → Notion research table',
  steps: 4,
  runs: 128,
  success: '96%',
  last: '2 hours ago'
}, {
  name: 'New lead → enriched CRM record',
  steps: 6,
  runs: 341,
  success: '99%',
  last: '18 minutes ago'
}, {
  name: 'Support thread → Linear ticket',
  steps: 3,
  runs: 87,
  success: '92%',
  last: 'Yesterday'
}, {
  name: 'Invoice → finance tracker row',
  steps: 5,
  runs: 64,
  success: '100%',
  last: '4 days ago'
}];
const SKILLS = [{
  name: 'File a Linear bug from a support thread',
  used: 'Used 44 times',
  learned: 'Recorded by Priya'
}, {
  name: 'Build a weekly pipeline recap',
  used: 'Used 31 times',
  learned: 'Recorded by Sarah'
}, {
  name: 'Enrich a lead from a company domain',
  used: 'Used 128 times',
  learned: 'Recorded by Marcus'
}, {
  name: 'Draft a renewal follow-up',
  used: 'Used 19 times',
  learned: 'Recorded by Sarah'
}];
const POWER_UPS = [{
  name: 'Computer use',
  desc: 'Operates sites that have no API, in a sandboxed browser.',
  on: true
}, {
  name: 'Deep research',
  desc: 'Multi-source research runs that cite every claim.',
  on: true
}, {
  name: 'Voice notes',
  desc: 'Transcribes and acts on voice messages.',
  on: false
}, {
  name: 'Code interpreter',
  desc: 'Runs analysis on spreadsheets and CSV exports.',
  on: false
}];
const FILES = [{
  name: 'Q3 Board Review.pdf',
  meta: '24 slides',
  size: '4.2 MB',
  group: 'Leadership',
  visibility: 'Shared',
  when: 'Yesterday'
}, {
  name: 'Acme MSA redlines.docx',
  meta: '18 pages',
  size: '0.24 MB',
  group: 'Leadership',
  visibility: 'Private',
  when: '2 days ago'
}, {
  name: 'Pricing model v4.xlsx',
  meta: '9 sheets',
  size: '1.1 MB',
  group: 'Product & Engineering',
  visibility: 'Shared',
  when: 'Last week'
}, {
  name: 'Meeting notes — Northwind.md',
  meta: '2 pages',
  size: '0.03 MB',
  group: 'Leadership',
  visibility: 'Private',
  when: '3 days ago'
}, {
  name: 'Brand guidelines.pdf',
  meta: '32 pages',
  size: '8.4 MB',
  group: 'Marketing',
  visibility: 'Shared',
  when: 'Last month'
}];
const TABLES = [{
  name: 'Pipeline',
  rows: '1,284 rows',
  cols: '18 columns',
  source: 'Salesforce',
  group: 'Revenue'
}, {
  name: 'Content calendar',
  rows: '96 rows',
  cols: '11 columns',
  source: 'Notion',
  group: 'Marketing'
}, {
  name: 'Competitor research',
  rows: '312 rows',
  cols: '14 columns',
  source: 'Giga native',
  group: 'Marketing'
}, {
  name: 'Invoice tracker',
  rows: '208 rows',
  cols: '9 columns',
  source: 'Airtable',
  group: 'Operations'
}];

// Per-group counts. Deep, muted tile colours so seven monograms stay
// distinguishable without turning the list into a paint chart.
const GROUP_DETAIL: Record<string, {
  path: string;
  color: string;
  members: number;
  chats: number;
  tracks: number;
  credentials: number;
  files: number;
}> = {
  'Product & Engineering': {
    path: 'spaces/product-engineering',
    color: '#1F3A5F',
    members: 18,
    chats: 214,
    tracks: 9,
    credentials: 14,
    files: 342
  },
  Marketing: {
    path: 'spaces/marketing',
    color: '#22303C',
    members: 9,
    chats: 89,
    tracks: 13,
    credentials: 8,
    files: 208
  },
  'Company-wide': {
    path: 'spaces/company-wide',
    color: '#8E1F45',
    members: 42,
    chats: 79,
    tracks: 1,
    credentials: 5,
    files: 86
  },
  Revenue: {
    path: 'spaces/revenue',
    color: '#332C7A',
    members: 12,
    chats: 96,
    tracks: 2,
    credentials: 9,
    files: 164
  },
  'Customer Success': {
    path: 'spaces/customer-success',
    color: '#8C2340',
    members: 8,
    chats: 54,
    tracks: 2,
    credentials: 7,
    files: 112
  },
  Operations: {
    path: 'spaces/operations',
    color: '#4A3A24',
    members: 5,
    chats: 17,
    tracks: 2,
    credentials: 6,
    files: 88
  },
  Leadership: {
    path: 'spaces/leadership',
    color: '#3A2C4A',
    members: 4,
    chats: 35,
    tracks: 4,
    credentials: 5,
    files: 61
  },
  Demo: {
    path: 'spaces/entropy',
    color: '#7A1F32',
    members: 4,
    chats: 4,
    tracks: 1,
    credentials: 1,
    files: 12
  }
};
const BROWSER_SITES = [{
  name: 'portal.acme-legal.com',
  desc: 'Downloads countersigned contracts',
  runs: '12 runs'
}, {
  name: 'app.quickbooks.com',
  desc: 'Pulls invoice status for the finance tracker',
  runs: '64 runs'
}, {
  name: 'suppliers.northwind.io',
  desc: 'Checks stock levels weekly',
  runs: '8 runs'
}];

// ── Small shared pieces ──────────────────────────────────────────────────────

function Card({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="rounded-xl bg-app-surface p-5 shadow-[0_1px_2px_rgba(20,20,24,0.05)]" data-id="element-3989" data-mp="19ftbvxyrrwjw">{children}</div>;
}
function Row({
  children
}: {
  children: React.ReactNode;
}) {
  return <li className="flex items-center gap-4 border-b border-app-divider px-5 py-4 transition-colors duration-200 ease-out last:border-b-0 hover:bg-app-hover" data-id="element-3990" data-mp="33tyntei7cy23">
      {children}
    </li>;
}
function List({
  children
}: {
  children: React.ReactNode;
}) {
  return <ul className="overflow-hidden rounded-xl bg-app-surface shadow-[0_1px_2px_rgba(20,20,24,0.05)]" data-id="element-3991" data-mp="3hohonlihztia">
      {children}
    </ul>;
}
function StatusDot({
  on
}: {
  on: boolean;
}) {
  return <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${on ? 'bg-emerald-500' : 'bg-neutral-300'}`} data-id="element-3992" data-mp="3rkuh6g2b41bg" />;
}
function ToolLogo({
  slug,
  size = 15
}: {
  slug: string;
  size?: number;
}) {
  return <img src={logo(slug)} alt="" aria-hidden="true" style={{
    width: size,
    height: size
  }} className="object-contain" data-id="element-3993" data-mp="4h6buvx395ml" />;
}

// ── The view itself ──────────────────────────────────────────────────────────

export function WorkspaceViews({
  view,
  onOpenTrack
}: {
  view: WorkspaceViewId;
  onOpenTrack?: (trackName: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [connTab, setConnTab] = useState<'all' | 'popular' | 'connected'>('all');
  const [connCategory, setConnCategory] = useState<ConnCategory | 'all'>('all');
  const [connCatOpen, setConnCatOpen] = useState(false);
  const [connGroup, setConnGroup] = useState<string>('all');
  const [connPage, setConnPage] = useState(1);
  const [customOpen, setCustomOpen] = useState(false);
  const [customType, setCustomType] = useState<(typeof CREDENTIAL_TYPES)[number]['id'] | null>(null);
  const meta = VIEW_META[view];
  // Pulses carries its own list + detail surface, so it owns the whole pane.
  if (view === 'pulses') return <PulsesView data-id="element-3994" data-mp="23n37q4569ely" />;
  const searchable = view === 'tracks' || view === 'chats' || view === 'files' || view === 'connections' || view === 'groups';
  const q = query.trim().toLowerCase();
  return <div className="mx-auto max-w-5xl px-6 pb-16 pt-16 md:px-8 md:pt-14" data-id="element-3995" data-mp="s7my15rydmwv">
      <header className="flex flex-wrap items-end justify-between gap-4 pb-7" data-id="element-3996" data-mp="3opc5l9ent897">
        <div data-id="element-3997" data-mp="3jyvp1gfjl6im">
          <h1 className="text-[22px] font-semibold tracking-[-0.015em] text-app" data-id="element-3998" data-mp="3jz5ft68evhpd">
            {meta.title}
          </h1>
          <p className="mt-1.5 max-w-[68ch] text-[14.5px] leading-6 text-app-muted" data-id="element-3999" data-mp="3ez0vf54du2tv">{meta.blurb}</p>
        </div>
        {view === 'connections' ?
      /* Anything without a first-party integration comes in here, so
      the control names the secret it needs rather than just "Add". */
      <div className="relative" data-id="element-4000" data-mp="6jhaquhs1sqf">
            <button type="button" onClick={() => setCustomOpen(o => !o)} aria-expanded={customOpen} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-app bg-app-surface px-3.5 text-[13.5px] font-semibold text-app transition-colors duration-200 ease-out hover:border-app-strong hover:bg-app-hover" data-id="element-4001" data-mp="6kij2r7es14g">
              <Plus size={14} strokeWidth={2.5} data-id="element-4002" data-mp="2n6jqcljhcrbw" />
              Add custom
              <ChevronDown size={14} className={`text-app-subtle transition-transform duration-200 ${customOpen ? 'rotate-180' : ''}`} data-id="element-4003" data-mp="2i7ypql0bg5mx" />
            </button>
            {customOpen && <>
                <div className="fixed inset-0 z-30" onClick={() => setCustomOpen(false)} data-id="element-4004" data-mp="12rdlij55zdel" />
                <div className="absolute right-0 top-[calc(100%+6px)] z-40 w-[280px] overflow-hidden rounded-xl border border-app bg-app-surface shadow-[0_20px_50px_-20px_rgba(20,20,24,0.28)]" data-id="element-4005" data-mp="16r4fswtwgbnv">
                  <p className="border-b border-app-divider px-4 py-2.5 text-[12px] font-semibold text-app-muted" data-id="element-4006" data-mp="1ggxhn648pcex">
                    Credential type
                  </p>
                  {CREDENTIAL_TYPES.map(t => <button key={t.id} type="button" onClick={() => {
              setCustomType(t.id);
              setCustomOpen(false);
            }} className="block w-full px-4 py-3 text-left transition-colors duration-200 ease-out hover:bg-app-hover" data-id="element-4007" data-mp="1qh7wsw9u4twq">
                      <span className="block text-[14px] font-medium text-app" data-id="element-4008" data-mp="3qvvh6oneju">
                        {t.label}
                      </span>
                      <span className="mt-0.5 block text-[12.5px] text-app-muted" data-id="element-4009" data-mp="13ffr1twqdpq1">
                        {t.desc}
                      </span>
                    </button>)}
                </div>
              </>}
          </div> : meta.action && <button type="button" onClick={view === 'tracks' ? () => onOpenTrack?.('Inbound leads → qualified pipeline') : undefined} className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-app-accent px-3.5 text-[13.5px] font-semibold text-white transition-colors duration-200 ease-out hover:bg-[var(--app-accent-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--app-accent)] focus-visible:ring-offset-2" data-id="element-4010" data-mp="1cd85ywyjivcw">
              <Plus size={14} strokeWidth={2.5} data-id="element-4011" data-mp="1r3yyk77yhyh2" />
              {meta.action}
            </button>}
      </header>

      {/* The form for the chosen credential type, inline above the list so the
       catalogue stays visible behind the decision. */}
      {view === 'connections' && customType && (() => {
      const t = CREDENTIAL_TYPES.find(c => c.id === customType)!;
      return <div className="mb-5 rounded-xl bg-app-surface p-5 shadow-[0_1px_2px_rgba(20,20,24,0.05)]" data-id="element-4012" data-mp="2ops0v035p96o">
              <div className="flex items-start justify-between gap-3" data-id="element-4013" data-mp="2aqal1a7u969g">
                <div data-id="element-4014" data-mp="2as3hjwoqma3q">
                  <h2 className="text-[15px] font-semibold text-app" data-id="element-4015" data-mp="2ercbb1js27za">
                    Add custom tool · {t.label}
                  </h2>
                  <p className="mt-1 text-[13px] text-app-muted" data-id="element-4016" data-mp="20zfnh9p56k8p">{t.desc}</p>
                </div>
                <button type="button" onClick={() => setCustomType(null)} aria-label="Cancel" className="text-app-subtle transition-colors duration-200 ease-out hover:text-app" data-id="element-4017" data-mp="25rpl7bwi0olj">
                  <X size={16} data-id="element-4018" data-mp="1w0csyj6sfwez" />
                </button>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2" data-id="element-4019" data-mp="1i1d71jantvmi">
                <label className="block sm:col-span-2" data-id="element-4020" data-mp="1i24zil8ynd8k">
                  <span className="mb-1.5 block text-[13px] font-medium text-app" data-id="element-4021" data-mp="1n0ppzsyeuijt">
                    Name
                  </span>
                  <input placeholder="What should Giga call this?" className="h-10 w-full rounded-lg border border-app px-3 text-[14px] text-app outline-none transition-colors duration-200 ease-out placeholder:text-app-subtle focus:border-app-accent" data-id="element-4022" data-mp="1ez8nvbdf3rg4" />
                </label>
                {t.fields.map(f => <label key={f.label} className={`block ${t.fields.length === 1 ? 'sm:col-span-2' : ''}`} data-id="element-4023" data-mp="117u3p17rxk7h">
                    <span className="mb-1.5 block text-[13px] font-medium text-app" data-id="element-4024" data-mp="wak5ox6bk081">
                      {f.label}
                    </span>
                    <input type={f.type} placeholder={f.placeholder} className="h-10 w-full rounded-lg border border-app px-3 text-[14px] text-app outline-none transition-colors duration-200 ease-out placeholder:text-app-subtle focus:border-app-accent" data-id="element-4025" data-mp="mat7aken112o" />
                  </label>)}
              </div>
              <div className="mt-4 flex items-center justify-between gap-4 border-t border-app-divider pt-3.5" data-id="element-4026" data-mp="2cns82rs0bi2d">
                <p className="flex items-center gap-1.5 text-[12.5px] text-app-muted" data-id="element-4027" data-mp="3ulx7ilywq0mv">
                  <Lock size={12} className="text-app-subtle" data-id="element-4028" data-mp="3ekm1uqzr115" />
                  Stored in the vault. Never shown in a chat.
                </p>
                <button type="button" className="h-9 rounded-lg bg-app-accent px-4 text-[13.5px] font-semibold text-white transition-colors duration-200 ease-out hover:bg-[var(--app-accent-strong)]" data-id="element-4029" data-mp="862oyockrbor">
                  Save credential
                </button>
              </div>
            </div>;
    })()}

      {searchable && <div className="relative mb-5" data-id="element-4030" data-mp="3gngqa13f4rqh">
          <Search size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-app-subtle" data-id="element-4031" data-mp="3kmg1u4jnnmqw" />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder={`Search ${meta.title.toLowerCase()}…`} className="h-10 w-full rounded-lg border border-app bg-app-surface pl-10 pr-4 text-[14px] text-app outline-none transition-colors duration-200 ease-out placeholder:text-app-subtle focus:border-app-accent" data-id="element-4032" data-mp="3pn3q4igi1phf" />
        </div>}

      {/* Assistants — cards, because each one has a brief worth reading. */}
      {view === 'assistants' && <div className="grid gap-3 sm:grid-cols-2" data-id="element-4033" data-mp="2wzpf6stdl76e">
          {ASSISTANTS.map(a => <Card key={a.name} data-id="element-4034" data-mp="3awcqk0m2rzvu">
              <div className="flex items-start justify-between gap-3" data-id="element-4035" data-mp="35y1f5lg4thbi">
                <span className="flex items-center gap-2" data-id="element-4036" data-mp="35yuc5upagsgg">
                  <StatusDot on={a.active} data-id="element-4037" data-mp="2i7owpgn9g26o" />
                  <span className="text-[15px] font-semibold text-app" data-id="element-4038" data-mp="2i6dxzv8o9kmf">
                    {a.name}
                  </span>
                </span>
                <button type="button" aria-label={`More options for ${a.name}`} className="rounded-md p-1 text-app-subtle transition-colors duration-200 ease-out hover:bg-app-hover hover:text-app" data-id="element-4039" data-mp="2ryjmguzptjwv">
                  <MoreHorizontal size={16} data-id="element-4040" data-mp="1zh7azw0ui6ut" />
                </button>
              </div>
              <p className="mt-1.5 flex items-center gap-1.5 text-[12.5px] text-app-muted" data-id="element-4041" data-mp="1uje4t2zjlcuj">
                {a.private ? <Lock size={12} className="shrink-0 text-app-subtle" data-id="element-4042" data-mp="a0lhlrl584ad" /> : <Users size={12} className="shrink-0 text-app-subtle" data-id="element-4043" data-mp="9yqy8hpisk8e" />}
                <span className="truncate" data-id="element-4044" data-mp="et4vkpnqt1fp">{a.group}</span>
                {a.private && <span className="shrink-0 rounded-md bg-app-subtle px-1.5 py-px text-[11px] font-medium text-app-muted" data-id="element-4045" data-mp="10ygz808n2id">
                    Private
                  </span>}
              </p>
              <p className="mt-3 text-[14px] leading-6 text-app-muted" data-id="element-4046" data-mp="sqtdbomijsgj">
                {a.desc}
              </p>
              <div className="mt-4 flex items-center justify-between gap-3 border-t border-app-divider pt-3" data-id="element-4047" data-mp="xpd4q4jvlwga">
                <span className="flex items-center gap-1.5" data-id="element-4048" data-mp="jpva38jwfoz0">
                  {a.tools.map(t => <ToolLogo key={t} slug={t} data-id="element-4049" data-mp="ooyd6ngbjl4c" />)}
                </span>
                <span className="text-[12.5px] text-app-subtle" data-id="element-4050" data-mp="34h5ib7v6knnz">{a.runs}</span>
              </div>
            </Card>)}
        </div>}

      {view === 'connections' && <>
          {/* Three ways in: what most teams start with, what's already live,
           and the full catalogue. Counts sit inline so the tabs double as a
           read on how far along the workspace is. */}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-app-divider" data-id="element-4051" data-mp="1fdotjbab53">
            <div className="flex items-center gap-5" data-id="element-4052" data-mp="3m699khyt13a1">
              {([['all', 'All integrations', CONNECTIONS.length], ['popular', 'Popular', CONNECTIONS.filter(c => c.popular).length], ['connected', 'Connected', CONNECTIONS.filter(c => c.accounts > 0).length]] as const).map(([id, label, count]) => <button key={id} type="button" onClick={() => {
            setConnTab(id);
            setConnPage(1);
          }} className={`-mb-px flex items-center gap-1.5 border-b-2 pb-2.5 text-[14px] font-semibold transition-colors duration-200 ease-out ${connTab === id ? 'border-app-accent text-app' : 'border-transparent text-app-muted hover:text-app'}`} data-id="element-4053" data-mp="irwrzt3ed46x">
                  {label}
                  <span className="text-[12.5px] font-medium text-app-subtle" data-id="element-4054" data-mp="11mljbcy87g3m">
                    {count}
                  </span>
                </button>)}
            </div>
            {/* Category narrows a long catalogue by the kind of work a tool does,
             which is how people look for one they haven't connected yet. */}
            <div className="relative -mb-px pb-1.5" data-id="element-4055" data-mp="1nxd7x1rrp5az">
              <button type="button" onClick={() => setConnCatOpen(o => !o)} aria-expanded={connCatOpen} className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[13px] font-medium text-app-muted transition-colors duration-200 ease-out hover:text-app" data-id="element-4056" data-mp="1nuqwus5o47o0">
                {connCategory === 'all' ? 'Category' : connCategory}
                <ChevronDown size={14} className={`text-app-subtle transition-transform duration-200 ${connCatOpen ? 'rotate-180' : ''}`} data-id="element-4057" data-mp="26n1tw5dn5be7" />
              </button>
              {connCatOpen && <>
                  <div className="fixed inset-0 z-30" onClick={() => setConnCatOpen(false)} data-id="element-4058" data-mp="2fnq8p2auzfs5" />
                  <div className="absolute right-0 top-[calc(100%+2px)] z-40 w-[190px] overflow-hidden rounded-xl border border-app bg-app-surface py-1 shadow-[0_20px_50px_-20px_rgba(20,20,24,0.28)]" data-id="element-4059" data-mp="3idf1002ht8n7">
                    {(['all', ...CONN_CATEGORIES] as const).map(cat => <button key={cat} type="button" onClick={() => {
                setConnCategory(cat as ConnCategory | 'all');
                setConnCatOpen(false);
                setConnPage(1);
              }} className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-[13.5px] font-medium text-app transition-colors duration-200 ease-out hover:bg-app-hover" data-id="element-4060" data-mp="38giufzw5mwla">
                        <span className="truncate" data-id="element-4061" data-mp="3rdjynihvwso6">
                          {cat === 'all' ? 'All categories' : cat}
                        </span>
                        <span className="flex shrink-0 items-center gap-1.5" data-id="element-4062" data-mp="nwl8qrrria1s">
                          <span className="text-[12px] text-app-subtle" data-id="element-4063" data-mp="sntlnxsawdjp">
                            {cat === 'all' ? CONNECTIONS.length : CONNECTIONS.filter(c => c.category === cat).length}
                          </span>
                          {connCategory === cat && <Check size={13} strokeWidth={3} className="text-app-accent" data-id="element-4064" data-mp="28ecnm6gj5a7x" />}
                        </span>
                      </button>)}
                  </div>
                </>}
            </div>
          </div>

          {/* Connected only: filter by the group that owns the credential.
           Monogram tiles rather than a dropdown — with a handful of groups the
           colours are recognised faster than names are read. */}
          {connTab === 'connected' && <div className="mb-4 flex items-center gap-1.5 overflow-x-auto pb-1" data-id="element-4065" data-mp="23gklykq329qj">
              <button type="button" onClick={() => {
          setConnGroup('all');
          setConnPage(1);
        }} aria-label="All groups" className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors duration-200 ease-out ${connGroup === 'all' ? 'bg-app-subtle text-app' : 'text-app-subtle hover:bg-app-hover hover:text-app'}`} data-id="element-4066" data-mp="1zepjve0bvsbp">
                <Grid3x3 size={15} data-id="element-4067" data-mp="27kta3v2nymz7" />
              </button>
              {GROUP_NAMES.filter(g => CONNECTIONS.some(c => c.accounts > 0 && CONN_GROUP[c.name] === g)).map(g => {
          const n = CONNECTIONS.filter(c => c.accounts > 0 && CONN_GROUP[c.name] === g).length;
          return <button key={g} type="button" onClick={() => {
            setConnGroup(g);
            setConnPage(1);
          }} title={`${g} · ${n} connected`} className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-[11px] font-bold tracking-tight text-white transition-transform duration-200 ease-out ${connGroup === g ? 'ring-2 ring-[var(--app-text)] ring-offset-2' : 'opacity-75 hover:opacity-100'}`} style={{
            backgroundColor: GROUP_DETAIL[g].color
          }} data-id="element-4068" data-mp="2lhyelqmqbdsc">
                      {g.split(' ').slice(0, 2).map(w => w[0]).join('')}
                      <span className="absolute -right-1 -top-1 flex h-[15px] min-w-[15px] items-center justify-center rounded-full border-2 border-white bg-app-text px-1 text-[9.5px] font-bold text-white" data-id="element-4069" data-mp="1a1ru7zb2tui1">
                        {n}
                      </span>
                    </button>;
        })}
            </div>}

          {(() => {
        const matches = CONNECTIONS.filter(c => connTab === 'all' || (connTab === 'popular' ? c.popular : c.accounts > 0)).filter(c => connTab !== 'connected' || connGroup === 'all' || CONN_GROUP[c.name] === connGroup).filter(c => connCategory === 'all' || c.category === connCategory).filter(c => !q || c.name.toLowerCase().includes(q) || c.desc.toLowerCase().includes(q));
        const pages = Math.max(1, Math.ceil(matches.length / CONN_PAGE_SIZE));
        const page = Math.min(connPage, pages);
        const start = (page - 1) * CONN_PAGE_SIZE;
        const shown = matches.slice(start, start + CONN_PAGE_SIZE);
        return <>
                {/* Count first, then pagination — you need to know how big the
                 result is before deciding whether to page or refine. */}
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3" data-id="element-4070" data-mp="20wg03v1yprho">
                  <p className="text-[12.5px] text-app-muted" data-id="element-4071" data-mp="1m7tghh5aydkn">
                    {matches.length === 0 ? 'No integrations match' : <>
                        Showing {start + 1}–{Math.min(start + CONN_PAGE_SIZE, matches.length)} of{' '}
                        {matches.length} integration
                        {matches.length === 1 ? '' : 's'}
                      </>}
                  </p>
                  {pages > 1 && <div className="flex items-center gap-1" data-id="element-4072" data-mp="139h29gzjadi7">
                      <button type="button" onClick={() => setConnPage(p => Math.max(1, p - 1))} disabled={page === 1} className="rounded-lg px-2.5 py-1.5 text-[12.5px] font-semibold text-app transition-colors duration-200 ease-out hover:bg-app-hover disabled:pointer-events-none disabled:text-app-subtle" data-id="element-4073" data-mp="13b20bgl24ekt">
                        Previous
                      </button>
                      <span className="px-1.5 text-[12.5px] text-app-muted" data-id="element-4074" data-mp="qzhlk4z1k1vt">
                        Page {page} of {pages}
                      </span>
                      <button type="button" onClick={() => setConnPage(p => Math.min(pages, p + 1))} disabled={page === pages} className="rounded-lg px-2.5 py-1.5 text-[12.5px] font-semibold text-app transition-colors duration-200 ease-out hover:bg-app-hover disabled:pointer-events-none disabled:text-app-subtle" data-id="element-4075" data-mp="10o9cc9sd8g55">
                        Next
                      </button>
                    </div>}
                </div>

                {matches.length === 0 ? <div className="rounded-xl bg-app-surface px-6 py-12 text-center shadow-[0_1px_2px_rgba(20,20,24,0.05)]" data-id="element-4076" data-mp="1jm2c537p09yb">
                    <p className="text-[14.5px] font-semibold text-app" data-id="element-4077" data-mp="1em7mt43kfsfd">
                      Nothing here
                    </p>
                    <p className="mx-auto mt-1.5 max-w-[38ch] text-[13.5px] leading-relaxed text-app-muted" data-id="element-4078" data-mp="3gfx9o6bakaoh">
                      Try another category, or add it yourself with a custom
                      credential.
                    </p>
                  </div> :
          /* Two columns: a catalogue is scanned for a name, and a single
           tall column makes the eye travel much further to find one. */
          <div className="grid gap-2 sm:grid-cols-2" data-id="element-4079" data-mp="nyg62dgxpat3">
                    {shown.map(c => <div key={c.name} className="group flex items-center gap-3 rounded-xl bg-app-surface px-4 py-3.5 shadow-[0_1px_2px_rgba(20,20,24,0.05)] transition-colors duration-200 ease-out hover:bg-app-hover" data-id="element-4080" data-mp="sxizf0njxsl8">
                        <ToolLogo slug={c.slug} size={20} data-id="element-4081" data-mp="11pb8b6wxgtnp" />
                        <span className="min-w-0 flex-1" data-id="element-4082" data-mp="11rxep383134c">
                          <span className="block truncate text-[14.5px] font-medium text-app" data-id="element-4083" data-mp="16ncqzqqfyqyn">
                            {c.name}
                          </span>
                          {/* Accounts, not a boolean — two accounts is a
                           materially different state. The specific account is
                           held back until hover so the grid stays quiet. */}
                          {c.accounts > 0 ? <>
                              <span className="mt-0.5 flex items-center gap-1.5 text-[12.5px] text-app-muted group-hover:hidden" data-id="element-4084" data-mp="2r6qa0gr8ltse">
                                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" data-id="element-4085" data-mp="2m8f0y88rnnb8" />
                                {c.accounts} account
                                {c.accounts === 1 ? '' : 's'} connected
                              </span>
                              <span className="mt-0.5 hidden truncate text-[12.5px] text-app-muted group-hover:block" data-id="element-4086" data-mp="3eync3g4vx7gb">
                                {c.accounts === 1 && c.account ? c.account : `${c.category} · managed in ${CONN_GROUP[c.name] ?? 'Everyone'}`}
                              </span>
                            </> : <span className="mt-0.5 block truncate text-[12.5px] text-app-muted" data-id="element-4087" data-mp="1pr7h8ojpeqg">
                              {c.desc}
                            </span>}
                        </span>
                        <button type="button" className="shrink-0 rounded-lg px-2.5 py-1.5 text-[12.5px] font-semibold text-app opacity-0 transition-opacity duration-200 ease-out hover:bg-app-hover group-hover:opacity-100" data-id="element-4088" data-mp="3ayaj6hjcvbol">
                          {c.accounts > 0 ? 'Manage' : 'Connect'}
                        </button>
                      </div>)}
                  </div>}
              </>;
      })()}
        </>}

      {/* Groups — the counts ARE the content: they tell you at a glance which
       group actually holds the company's knowledge. Metrics sit as quiet
       pills on the right so scanning the names stays effortless. */}
      {view === 'groups' && <List data-id="element-4089" data-mp="3kv6wd0k78lzh">
          {GROUP_NAMES.filter(g => !q || g.toLowerCase().includes(q)).map(g => {
        const d = GROUP_DETAIL[g];
        const shared = !g.toLowerCase().includes('private');
        return <Row key={g} data-id="element-4090" data-mp="29i4fe4e6tues">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-[12px] font-bold tracking-tight text-white" style={{
            backgroundColor: d.color
          }} data-id="element-4091" data-mp="29eymee2fu8ds">
                    {g.split(' ').slice(0, 2).map(w => w[0]).join('')}
                  </span>
                  <span className="min-w-0 flex-1" data-id="element-4092" data-mp="2aw4asjo31h94">
                    <span className="flex items-center gap-2" data-id="element-4093" data-mp="260gk1ztycg1i">
                      <span className="truncate text-[14.5px] font-medium text-app" data-id="element-4094" data-mp="1i9jldor63f87">
                        {g}
                      </span>
                      {!shared && <span className="shrink-0 rounded-md bg-app-subtle px-2 py-0.5 text-[11px] font-medium text-app-muted" data-id="element-4095" data-mp="1e9skgs7xfipy">
                          Private
                        </span>}
                    </span>
                    <span className="mt-0.5 block truncate font-mono text-[12px] text-app-subtle" data-id="element-4096" data-mp="1n6t8gxr33esv">
                      {d.path}
                    </span>
                  </span>
                  <span className="hidden shrink-0 items-center gap-1 sm:flex" data-id="element-4097" data-mp="ujp7d3v43kmd">
                    {([[Users, d.members, 'members'], [MessageSquare, d.chats, 'chats'], [Layers, d.tracks, 'tracks'], [KeyRound, d.credentials, 'credentials'], [Copy, d.files, 'files']] as const).map(([Icon, n, label]) => <span key={label} title={`${n} ${label}`} className="inline-flex items-center gap-1 rounded-full bg-app-subtle px-2 py-1 text-[12px] font-medium text-app-muted" data-id="element-4098" data-mp="4smxyrw6tc78">
                        <Icon size={12} className="text-app-subtle" data-id="element-4099" data-mp="3997kc0rkgns7" />
                        {n}
                      </span>)}
                  </span>
                  <button type="button" aria-label={`Edit ${g}`} className="shrink-0 rounded-md p-1 text-app-subtle transition-colors duration-200 ease-out hover:bg-app-hover hover:text-app" data-id="element-4100" data-mp="3478f0lc9ngsm">
                    <Pencil size={15} data-id="element-4101" data-mp="3d7n4lww9ji1x" />
                  </button>
                </Row>;
      })}
        </List>}

      {view === 'tracks' && <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-id="element-4102" data-mp="2litnzy1y82rt">
          {TRACKS.filter(t => t.status === 'active').filter(t => !q || t.name.toLowerCase().includes(q)).map(t => <button key={t.name} type="button" onClick={() => onOpenTrack?.(t.name)} className="group rounded-xl bg-app-surface p-4 text-left shadow-[0_1px_2px_rgba(20,20,24,0.05)] transition-colors duration-200 ease-out hover:bg-app-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--app-accent)]" data-id="element-4103" data-mp="2bck3fuxf2bin">
                <span className="flex items-center justify-between gap-2" data-id="element-4104" data-mp="2u4l9bh8lz21k">
                  <Folder size={16} className="text-app-subtle transition-colors duration-200 ease-out group-hover:text-app-muted" data-id="element-4105" data-mp="2u3a7vo2my6dm" />
                  <StatusDot on data-id="element-4106" data-mp="2k7qbl0k5t5oc" />
                </span>
                <span className="mt-3 block truncate text-[14.5px] font-medium text-app" data-id="element-4107" data-mp="2k91ih98witjo">
                  {t.name}
                </span>
                <span className="mt-1 block truncate text-[12.5px] text-app-muted" data-id="element-4108" data-mp="18ra1lmrdjh8v">
                  {t.group} · {t.type}
                </span>
              </button>)}
        </div>}

      {view === 'chats' && <List data-id="element-4109" data-mp="3gdte55haby2e">
          {CHATS.filter(c => !q || c.name.toLowerCase().includes(q) || c.preview.toLowerCase().includes(q)).map(c => <Row key={c.name} data-id="element-4110" data-mp="3bfruaic1bupv">
                <span className="min-w-0 flex-1" data-id="element-4111" data-mp="3bgjpvt5mtsg5">
                  <span className="block truncate text-[14.5px] font-medium text-app" data-id="element-4112" data-mp="36moz65rm0d7k">
                    {c.name}
                  </span>
                  <span className="mt-0.5 block truncate text-[12.5px] text-app-muted" data-id="element-4113" data-mp="31mjgvg78c325">
                    {c.preview}
                  </span>
                </span>
                <span className="shrink-0 text-[12px] text-app-subtle" data-id="element-4114" data-mp="2sn63wdjwcwco">
                  {c.when}
                </span>
              </Row>)}
        </List>}

      {view === 'workflows' && <List data-id="element-4115" data-mp="2iqkyhr3ytj8b">
          {WORKFLOWS.map(w => <Row key={w.name} data-id="element-4116" data-mp="2ircjy93um0kv">
              <span className="min-w-0 flex-1" data-id="element-4117" data-mp="lxhr3jb72o11">
                <span className="block truncate text-[14.5px] font-medium text-app" data-id="element-4118" data-mp="lw6npklnviag">
                  {w.name}
                </span>
                <span className="mt-0.5 block text-[12.5px] text-app-muted" data-id="element-4119" data-mp="gwbuhaeeygm8">
                  {w.steps} steps · {w.runs} runs · {w.success} success · last{' '}
                  {w.last}
                </span>
              </span>
              <button type="button" aria-label={`Run ${w.name}`} className="h-8 shrink-0 rounded-lg border border-app px-3 text-[12.5px] font-semibold text-app transition-colors duration-200 ease-out hover:border-app-strong hover:bg-app-hover" data-id="element-4120" data-mp="y80lfzub11vn">
                Run
              </button>
            </Row>)}
        </List>}

      {view === 'skills' && <List data-id="element-4121" data-mp="aioi3glnro19">
          {SKILLS.map(s => <Row key={s.name} data-id="element-4122" data-mp="ajzjxavjmlpq">
              <GraduationCap size={16} className="shrink-0 text-app-subtle" data-id="element-4123" data-mp="fgpzzy7clhb5" />
              <span className="min-w-0 flex-1" data-id="element-4124" data-mp="kgceeaywx3lj">
                <span className="block truncate text-[14.5px] font-medium text-app" data-id="element-4125" data-mp="kh3x4l7ev6ue">
                  {s.name}
                </span>
                <span className="mt-0.5 block text-[12.5px] text-app-muted" data-id="element-4126" data-mp="1qyr26uyz6clb">
                  {s.learned}
                </span>
              </span>
              <span className="shrink-0 text-[12px] text-app-subtle" data-id="element-4127" data-mp="1thvuox2nvejm">
                {s.used}
              </span>
            </Row>)}
        </List>}

      {view === 'power-ups' && <div className="grid gap-3 sm:grid-cols-2" data-id="element-4128" data-mp="23eqtzi6rdiyy">
          {POWER_UPS.map(p => <Card key={p.name} data-id="element-4129" data-mp="23cnrhyof5xdc">
              <div className="flex items-start justify-between gap-3" data-id="element-4130" data-mp="1yfe4epoi5xso">
                <span className="text-[15px] font-semibold text-app" data-id="element-4131" data-mp="1yia34g86cqi6">
                  {p.name}
                </span>
                <span className={`mt-0.5 flex h-[18px] w-[32px] shrink-0 items-center rounded-full px-[3px] transition-colors duration-200 ease-out ${p.on ? 'justify-end bg-app-text' : 'justify-start bg-[var(--app-border-strong)]'}`} data-id="element-4132" data-mp="28ed7ztuev38t">
                  <span className="h-3 w-3 rounded-full bg-white" data-id="element-4133" data-mp="2h7q414nrukz4" />
                </span>
              </div>
              <p className="mt-2 text-[14px] leading-6 text-app-muted" data-id="element-4134" data-mp="2w47elto4zvkm">
                {p.desc}
              </p>
            </Card>)}
        </div>}

      {view === 'files' && <List data-id="element-4135" data-mp="3aq4sndii9reg">
          {FILES.filter(f => !q || f.name.toLowerCase().includes(q)).map(f => <Row key={f.name} data-id="element-4136" data-mp="3erq54xr4o3o4">
                <FolderOpen size={16} className="shrink-0 text-app-subtle" data-id="element-4137" data-mp="3eqorcliq932p" />
                <span className="min-w-0 flex-1" data-id="element-4138" data-mp="30t04j853nlv0">
                  <span className="block truncate text-[14.5px] font-medium text-app" data-id="element-4139" data-mp="30ub9hbaa6znp">
                    {f.name}
                  </span>
                  <span className="mt-0.5 block text-[12.5px] text-app-muted" data-id="element-4140" data-mp="2r1vp67bihyzn">
                    {f.meta} · {f.group}
                  </span>
                </span>
                {/* Size and who can see it — the two things you check before
                 sharing or deleting, so they sit on the row itself. */}
                <span className="hidden shrink-0 items-center gap-3 text-[12.5px] text-app-muted sm:flex" data-id="element-4141" data-mp="2n1vbiow8lrtn">
                  <span data-id="element-4142" data-mp="284ujsle1pwv6">{f.size}</span>
                  <span className="inline-flex items-center gap-1.5" data-id="element-4143" data-mp="285vvms2vyndy">
                    <Copy size={12} className="text-app-subtle" data-id="element-4144" data-mp="2d24ikrnbrkvo" />
                    {f.visibility}
                  </span>
                </span>
                <span className="shrink-0 text-[12px] text-app-subtle" data-id="element-4145" data-mp="1p7kn3q5h18be">
                  {f.when}
                </span>
              </Row>)}
        </List>}

      {view === 'tables' && <List data-id="element-4146" data-mp="1k63jhmvlr7r4">
          {TABLES.map(t => <Row key={t.name} data-id="element-4147" data-mp="1f9mz93rixdjb">
              <Database size={16} className="shrink-0 text-app-subtle" data-id="element-4148" data-mp="1fafvhasuwkn7" />
              <span className="min-w-0 flex-1" data-id="element-4149" data-mp="2cx56ub9g89lq">
                <span className="block truncate text-[14.5px] font-medium text-app" data-id="element-4150" data-mp="2cusrm5v2sdcd">
                  {t.name}
                </span>
                <span className="mt-0.5 block text-[12.5px] text-app-muted" data-id="element-4151" data-mp="22yfnsf5tgj3w">
                  {t.rows} · {t.cols} · {t.group}
                </span>
              </span>
              <span className="shrink-0 rounded-full bg-app-subtle px-2.5 py-1 text-[11.5px] font-medium text-app-muted" data-id="element-4152" data-mp="1y1p7pru0hnih">
                {t.source}
              </span>
            </Row>)}
        </List>}

      {view === 'browser' && <List data-id="element-4153" data-mp="2y82qs3ck2opn">
          {BROWSER_SITES.map(s => <Row key={s.name} data-id="element-4154" data-mp="2y94288xhhd7w">
              <Globe size={16} className="shrink-0 text-app-subtle" data-id="element-4155" data-mp="2y5q277calg3m" />
              <span className="min-w-0 flex-1" data-id="element-4156" data-mp="337ns2kf7795j">
                <span className="block truncate text-[14.5px] font-medium text-app" data-id="element-4157" data-mp="385z1wv5htjnr">
                  {s.name}
                </span>
                <span className="mt-0.5 block text-[12.5px] text-app-muted" data-id="element-4158" data-mp="3cxqsh5onfpdw">
                  {s.desc}
                </span>
              </span>
              <span className="shrink-0 text-[12px] text-app-subtle" data-id="element-4159" data-mp="3hy4qrw0g7kyq">
                {s.runs}
              </span>
            </Row>)}
        </List>}

      {/* Viewer — one live run, because that is the whole point of the screen. */}
      {view === 'viewer' && <div className="overflow-hidden rounded-xl bg-app-surface shadow-[0_1px_2px_rgba(20,20,24,0.05)]" data-id="element-4160" data-mp="3vuia7aojlwfu">
          <div className="flex items-center justify-between gap-3 border-b border-app-divider px-5 py-4" data-id="element-4161" data-mp="1wjquw1da0btt">
            <span className="flex items-center gap-2" data-id="element-4162" data-mp="21i25iebeaqd3">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-app-accent" data-id="element-4163" data-mp="79eqvygjrmqh" />
              <span className="text-[14.5px] font-semibold text-app" data-id="element-4164" data-mp="2ale68933zrd">
                New lead → enriched CRM record
              </span>
            </span>
            <span className="text-[12.5px] text-app-muted" data-id="element-4165" data-mp="gyyetij4a6s7">
              Step 4 of 6 · 00:38
            </span>
          </div>
          <ol className="divide-y divide-[var(--app-divider)]" data-id="element-4166" data-mp="c2r5dfriid5i">
            {[{
          label: 'Read the inbound form submission',
          state: 'done'
        }, {
          label: 'Looked up the company domain',
          state: 'done'
        }, {
          label: 'Pulled headcount and funding from the web',
          state: 'done'
        }, {
          label: 'Writing the record to Salesforce',
          state: 'running'
        }, {
          label: 'Assigning an owner by territory',
          state: 'pending'
        }, {
          label: 'Posting a summary to #sales',
          state: 'pending'
        }].map(s => <li key={s.label} className="flex items-center gap-3 px-5 py-3.5" data-id="element-4167" data-mp="1c8v0084ntf93">
                {s.state === 'done' ? <Check size={14} strokeWidth={3} className="shrink-0 text-emerald-500" data-id="element-4168" data-mp="1h87iyhseu26e" /> : s.state === 'running' ? <span className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-app-accent border-t-transparent" data-id="element-4169" data-mp="138evcvc6k22n" /> : <span className="h-3.5 w-3.5 shrink-0 rounded-full border-2 border-app" data-id="element-4170" data-mp="17ajmnnf5omm4" />}
                <span className={`text-[14px] ${s.state === 'pending' ? 'text-app-subtle' : 'text-app'}`} data-id="element-4171" data-mp="1uyt2m8poug5c">
                  {s.label}
                </span>
              </li>)}
          </ol>
          <div className="flex items-center justify-between gap-3 border-t border-app-divider px-5 py-3.5" data-id="element-4172" data-mp="1m7aqq9zrymi2">
            <span className="text-[12.5px] text-app-muted" data-id="element-4173" data-mp="1q7ksfagjaqr4">
              412 credits spent so far
            </span>
            <button type="button" className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-app transition-colors duration-200 ease-out hover:text-app-accent" data-id="element-4174" data-mp="2dx5ei27zi44m">
              Open full trace
              <ArrowUpRight size={13} data-id="element-4175" data-mp="3k55rdeh1qqb8" />
            </button>
          </div>
        </div>}
    </div>;
}